import { IdCodeName } from './general'
import { CustomAttributeForm } from '@/hooks'
import { TablePermission, TableConstraint } from '@/rtkApi/mddApiSliceGenerated'

export interface TableData extends HistoryTableData {
	name: string
	code: string
	columns: TableColumn[]
	columnsLastId: number
	indexes: TableIndex[]
	indexesLastId: number
	constraints: TableConstraint[]
	constraintsLastId: number
	hasHistoryTable: boolean
	hasReferenceTable: boolean
	/** Table-level ODCS DQ rules (optional; column-level rules live on `TableColumn.odcsDqRules`). */
	odcsDqRules?: OdcsDqRule[]
	objectPermissions: TablePermission[]
	objectPermissionLastId: number
	historyTable?: HistoryTableData
	referenceTable?: HistoryTableData
	HISTORY?: HistoryTableData
}

export enum PartitioningStrategy {
	NO_PARTITION = 'no_partition',
	KEY = 'key',
	SOURCE_SYSTEM = 'source_system',
	SNAPSHOT_DATA = 'snapshot_data',
	SOURCE_AND_SNAPSHOT = 'source_and_snapshot',
	KEY_AND_SNAPSHOT = 'key_and_snapshot',
	SOURCE_AND_SNAPSHOT_AND_DATE_EFFECTIVE = 'source_and_snapshot_and_date_effective',
	KEY_AND_SNAPSHOT_AND_DATE_EFFECTIVE = 'key_and_snapshot_and_date_effective',
	SOURCE_SYSTEM_AND_VALID_TO = 'source_system_and_valid_to',
}

export type DqRuleDefinitionKind =
	| 'odcs_library'
	| 'sql'
	| 'expectation'
	| 'gx_expectation'
	| 'free_text'

export type DqRuleDimension =
	| 'accuracy'
	| 'completeness'
	| 'conformity'
	| 'consistency'
	| 'coverage'
	| 'timeliness'
	| 'uniqueness'

export type DqRuleSeverity = 'error' | 'warning' | 'info'

export interface ExpectationDefinitionRef {
	code?: string
	providerCode?: string
}

/** API expectation payload under {@link OdcsDqRule.expectation} (OpenAPI `Expectation`). */
export interface ExpectationPayload {
	definition?: ExpectationDefinitionRef
	kwargs?: Record<string, unknown>
	/** Legacy FE field; still read for older persisted rules. */
	formValues?: Record<string, unknown>
}

/** @deprecated Use {@link ExpectationPayload} */
export type GxExpectationPayload = ExpectationPayload

/** ODCS data-quality rule on a table or column (OpenAPI `DqRule`). */
export interface OdcsDqRule {
	uuid?: string
	name?: string
	description?: string
	dimension?: DqRuleDimension
	severity?: DqRuleSeverity
	definitionKind?: DqRuleDefinitionKind
	freeText?: string
	expectation?: ExpectationPayload
	/** Legacy field name; still read for older persisted rules. */
	gx?: ExpectationPayload | Record<string, unknown>
	library?: Record<string, unknown>
	sql?: { query?: string; operator?: unknown }
}

export interface HistoryTableData {
	businessDomainId?: number
	businessDomainName?: string
	code?: string
	columns?: TableColumn[]
	comment?: string
	constraints?: TableConstraint[]
	customAttributes?: CustomAttributeForm[]
	description?: string
	entityType?: TableOverview.EntityTypeEnum
	indexes?: TableIndex[]
	name?: string
	nonSyncFields?: (keyof HistoryTableData)[]
	objectSettings?: IdCodeName[]
	ownerId?: number
	ownerName?: string
	partitioningColumnCode?: string
	partitioningColumnId?: number
	partitioningStrategy?: PartitioningStrategy
	postTableScript?: string
	preTableScript?: string
	responsibleAnalystId?: number
	responsibleAnalystName?: string
	responsiblePersonId?: number
	responsiblePersonName?: string
	retentionStrategy?: TableOverview.RetentionStrategyEnum
	retentionStrategyEomUnit?: RetentionTimesEnum
	retentionStrategyEomValue?: number
	retentionStrategyFullHistoryUnit?: RetentionTimesEnum
	retentionStrategyFullHistoryValue?: number
	script?: string
	sequenceCode?: string
	sequenceColumnCode?: string
	status?: TableOverview.TableStatusEnum
	stereotypeCode?: string
	stereotypeId?: number
	subStereotypeId?: number
	uuid?: string
}

export interface TableColumn {
	id: number
	uuid?: string
	order?: number
	name: string
	code?: string
	description?: string
	comment?: string
	dataType?: string
	defaultValue?: string
	notNullFlag?: boolean
	foreignKey?: boolean
	primaryKey?: string
	stereotypeColumn?: boolean
	historyColumn?: boolean
	stereotype?: IdCodeName
	domain?: IdCodeName
	virtualColumnScript?: string
	technicalColumnId?: number
	customAttributes?: CustomAttributeForm[]
	/** ODCS DQ rules on this column; expectations use `definitionKind: 'expectation'`. */
	odcsDqRules?: OdcsDqRule[]
	/**
	 * Optional aggregate count when the API does not expand `odcsDqRules` yet.
	 * Prefer counting `expectation` entries in `odcsDqRules` when present.
	 */
	gxExpectationCount?: number
	rightAction?: string
}

export interface TableIndex {
	id: number
	order: number
	name: string
	code?: string
	isUnique: boolean
	physicalOptions?: string
	description?: string
	comment?: string
	columns: TableIndexColumn[]
}

export interface TableIndexColumn {
	code: string
	order: number
	sortType: TableIndexColumn.SortTypeEnum
}

export interface TableConstraintColumn {
	code: string
	uuid: string
	name?: string
	foreignColumnCode?: string
	foreignColumnUuid?: string
	sortType?: TableIndexColumn.SortTypeEnum
}

export namespace TableOverview {
	export enum EntityTypeEnum {
		AGGREGATE = 'aggregate',
		AUXILIARY = 'auxiliary',
		COMPOSITION = 'composition',
		FUNDAMENTAL = 'fundamental',
		METADATA = 'metadata',
		RELATION = 'relation',
		SNAPSHOT = 'snapshot',
		SPECIALISATION = 'specialisation',
		TRANSACTION = 'transaction',
	}

	export enum TableStatusEnum {
		LOGICAL_IN_PROGRESS = 'logical_in_progress',
		LOGICAL_COMPLETED = 'logical_completed',
		PHYSICAL_IN_PROGRESS = 'physical_in_progress',
		PHYSICAL_COMPLETED = 'physical_completed',
		FROZEN = 'frozen',
	}

	export enum RetentionStrategyEnum {
		INFINITE = 'infinite',
		KEEP_FULL_HISTORY = 'keep_full_history',
		KEEP_FULL_HISTORY_AND_EOM = 'keep_full_history_and_eom',
	}
}

export namespace TableIndexColumn {
	export enum SortTypeEnum {
		ASC = 'ASC',
		DESC = 'DESC',
	}
}

export enum RetentionTimesEnum {
	DAYS = 'day',
	MONTHS = 'month',
	YEARS = 'year',
}
